import * as React from 'react';
import styled from '@emotion/styled';
import { Fonts, Spacings } from '@styles/index';
import Checkbox from '@base/Checkbox/Checkbox';
import { TextField } from '@material-ui/core';
import CheckboxToggle from '@icon/CheckboxToggle';
import CheckboxChecked from '@icon/CheckboxChecked';

interface Option {
  id: number;
  rank: number;
  name: string;
}

interface IBatchOptionsListProps {
  options: Option[];
  selected: number[];
  onOptionsSelect: (options: number[]) => void;
  onDescriptionChange: (e: any) => void;
}

interface Selected {
  $selected: boolean;
}

interface Focused extends Selected {
  $focused: boolean;
}

const Wrapper = styled.div`
  margin-top: ${Spacings.SPACING_10B};
  margin-bottom: 112px;
  grid-column: span 10;
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_3B};
`;

const CheckboxWrapper = styled.div<Selected>`
  height: ${Spacings.SPACING_14B};
  border: ${Spacings.SPACING_1} solid
    ${({ $selected }) =>
      $selected ? 'var(--color-text-primary)' : 'var(--color-divider)'};
  display: flex;
  align-items: center;
  padding-left: ${Spacings.SPACING_4B};
  border-radius: ${Spacings.SPACING_2B};
  cursor: pointer;

  .MuiFormControlLabel-label {
    font-weight: ${Fonts.SEMIBOLD};
    font-size: ${Fonts.P1};
    line-height: 18px;
  }
  &:hover {
    background-color: var(--color-base-light-0);
    border: ${Spacings.SPACING_1} solid
      ${({ $selected }) =>
        $selected ? 'var(--color-text-primary)' : 'var(--color-base-light-0)'};
  }
`;

const StyTextField = styled(TextField)<Focused>`
  border: ${Spacings.SPACING_1} solid
    ${({ $selected, $focused }) =>
      $selected || $focused
        ? 'var(--color-text-primary)'
        : 'var(--color-divider)'};
  border-radius: ${Spacings.SPACING_2B};
  padding: 15px ${Spacings.SPACING_2B} 15px ${Spacings.SPACING_6B};
  margin-top: ${Spacings.SPACING_3B};

  .MuiInput-underline:before {
    border: 0;
  }
  .MuiInput-underline:after {
    border: 0;
  }
  .MuiInput-underline:hover:not(.Mui-disabled):before {
    border: 0;
  }

  .MuiInputBase-multiline {
    padding: 0;
  }

  textarea {
    color: var(--color-text-primary);
  }

  textarea::-webkit-input-placeholder {
    color: var(--color-text-secondary);
  }

  .MuiInputBase-input::-webkit-scrollbar {
    background-color: transparent;
    width: 10px;
  }

  .MuiInputBase-input::-webkit-scrollbar-thumb {
    background-color: none;
    border-radius: ${Spacings.SPACING_4B};
    border: ${Spacings.SPACING_2} solid var(--color-base-0);
  }

  .MuiInputBase-input:hover {
    &::-webkit-scrollbar-thumb {
      background-color: var(--color-i-gray-21);
    }
  }

  .MuiInputBase-input::-webkit-scrollbar-thumb:hover {
    background-color: var(--color-i-gray-21);
  }
`;

const BatchOptionsList: React.FC<IBatchOptionsListProps> = ({
  options,
  selected,
  onOptionsSelect,
  onDescriptionChange
}) => {
  const optionsLength = options.length;
  const lastOptionId = options[optionsLength - 1]?.id;
  const ref = React.useRef();
  const isOtherReasonSelected = selected.includes(lastOptionId);

  const [textFieldFocused, setTextFieldFocused] = React.useState(false);

  const onChange = (e, checked, id) => {
    e.stopPropagation();
    e.preventDefault();

    if (checked) {
      onOptionsSelect([...selected, id]);
    } else {
      const updatedSelected = [...selected];
      const index = updatedSelected.findIndex((value) => value === id);
      updatedSelected.splice(index, 1);
      onOptionsSelect(updatedSelected);
    }
  };

  const onFocus = () => {
    setTextFieldFocused(true);
    // @ts-ignore
    if (ref.current) ref.current.scrollIntoView();
  };

  const onBlur = () => {
    setTextFieldFocused(false);
  };

  React.useEffect(() => {
    if (isOtherReasonSelected && ref.current) {
      // @ts-ignore
      const childElements = ref.current.childNodes || [];
      const grandChildElements = (childElements[0] || {}).childNodes || [];
      const inputElement = grandChildElements[0];
      if (inputElement) inputElement.focus();
      // @ts-ignore
      ref.current.scrollIntoView();
    }
  }, [isOtherReasonSelected]);

  return (
    <Wrapper>
      {options.map((option) => {
        const { id, name } = option;
        const checked = selected.includes(id);

        return (
          <CheckboxWrapper
            $selected={checked}
            key={id}
            onClick={(e) => onChange(e, !checked, id)}
          >
            <Checkbox
              label={name}
              checked={checked}
              // @ts-ignore
              icon={
                <CheckboxToggle
                  color="var(--color-divider)"
                  fill="var(--color-base-1)"
                  height={Spacings.SPACING_6B}
                  width={Spacings.SPACING_6B}
                />
              }
              checkedIcon={
                <CheckboxChecked
                  color="var(--color-text-primary)"
                  fill="var(--color-base-1)"
                  height={Spacings.SPACING_6B}
                  width={Spacings.SPACING_6B}
                />
              }
            />
          </CheckboxWrapper>
        );
      })}
      <StyTextField
        placeholder="Please tell us more.."
        rows={4}
        multiline
        ref={ref}
        onChange={onDescriptionChange}
        onFocus={onFocus}
        onBlur={onBlur}
        $focused={textFieldFocused}
        $selected={isOtherReasonSelected}
      />
    </Wrapper>
  );
};

export default BatchOptionsList;
